import React from 'react';
import './Oauth2.css';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import { useFeatureFlags } from '../../features/feature-flags/presentation/FeatureFlagsContext';
import { useAuthContext } from '../../features/auth/presentation/LoginAuthContext';

// Client ID comes from the environment (REACT_APP_GOOGLE_CLIENT_ID). The
// button only renders when Google auth is flagged ON *and* a client ID is
// present — so a missing/misconfigured client ID degrades to "no Google
// button", never a crash. Email/password remains the always-available path.
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

/**
 * "Continue with Google" button.
 *
 * Built on `@react-oauth/google` (Google Identity Services) — the maintained
 * successor to the retired `gapi.auth2` platform library. On success Google
 * hands us `credentialResponse.credential`, a signed ID token, which we POST
 * to `/identity/google/` (via `login_google`) and exchange for our own JWT
 * session — the same session email/password login produces.
 *
 * Resilience: the whole thing is self-contained and fail-safe. If the feature
 * flag is off, the client ID is missing, or Google's script fails to load, the
 * component renders nothing and the surrounding login form is unaffected.
 */
const Oauth2Google = () => {
  const { features } = useFeatureFlags();
  const { login_google, setLoading, show_error } = useAuthContext();
  const isGoogleAuthEnabled = Boolean(features?.isGoogleAuthEnabled);

  if (!isGoogleAuthEnabled || !GOOGLE_CLIENT_ID) {
    return null;
  }

  const handleSuccess = (credentialResponse) => {
    const credential = credentialResponse && credentialResponse.credential;
    if (!credential) {
      show_error &&
        show_error('Google sign-in did not complete. Please try again.', true);
      return;
    }
    // `login_google` owns the loading state + token persistence + redirect.
    login_google(credential);
  };

  const handleError = () => {
    setLoading && setLoading(false, '');
    show_error &&
      show_error(
        'Google sign-in was cancelled or failed. Please try again.',
        true
      );
  };

  return (
    <div className="google_button_class">
      <GoogleOAuthProvider
        clientId={GOOGLE_CLIENT_ID}
        onScriptLoadError={() =>
          // Non-fatal: the button simply won't appear; email/password stays.
          console.warn('Google Identity Services script failed to load.')
        }
      >
        <GoogleLogin
          onSuccess={handleSuccess}
          onError={handleError}
          useOneTap={false}
          text="continue_with"
          shape="pill"
          size="large"
          width="320"
          logo_alignment="center"
        />
      </GoogleOAuthProvider>
    </div>
  );
};

export default Oauth2Google;
